import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import T2TTopNav from "../Layout/T2TTopNav";
import { getData, postData } from "../../../FetchApiServices";
import { useToast, usePermissions } from "../Layout/T2TUIProvider";

const T2TQueue = () => {
  const navigate          = useNavigate();
  const toast             = useToast();
  const { hasPermission } = usePermissions();
  const showTeam          = hasPermission("reports.view");

  const [activeTab, setActiveTab] = useState("queue");

  // BUG-35 follow-up: embedded executive snapshot at the top of the Queue page
  // for admin/CXO. Reuses the same metrics endpoint as Reports → Executive
  // Dashboard so the numbers can't drift.
  const [overviewMetrics, setOverviewMetrics] = useState(null);
  useEffect(() => {
    if (!showTeam) return;
    let cancelled = false;
    (async () => {
      const resp = await getData("t2t/reports/overview/metrics");
      if (!cancelled && !resp.error) setOverviewMetrics(resp.data?.data || null);
    })();
    return () => { cancelled = true; };
  }, [showTeam]);

  const snapshotTiles = overviewMetrics ? [
    {
      label: "Active Requests",
      value: overviewMetrics.active_requests ?? "—",
      color: "#2563EB",
      onClick: () => navigate("/t2t-reports?tab=tracker&status=in_progress"),
      hint: "Click to open the Request Tracker filtered to in-progress.",
    },
    {
      label: "Pending My Action",
      value: overviewMetrics.pending_my_action ?? "—",
      color: "#D97706",
      onClick: () => setActiveTab("queue"),
      hint: "Click to open your personal queue.",
    },
    {
      label: "Overdue / SLA Breached",
      value: overviewMetrics.overdue ?? "—",
      color: "#DC2626",
      onClick: () => navigate("/t2t-reports?tab=tracker&sla=breached"),
      hint: "Click to open the Request Tracker filtered to SLA-breached.",
    },
    {
      label: "Completed This Month",
      value: overviewMetrics.completed_this_month ?? "—",
      color: "#16A34A",
      onClick: () => navigate("/t2t-reports?tab=tracker&status=approved"),
      hint: "Click to open the Request Tracker filtered to approved.",
    },
  ] : [];

  return (
    <>
      <T2TTopNav title={activeTab === "queue" ? "My Queue" : "Team Overview"} />
      <div className="t2t-content">

        {/* BUG-35 follow-up: compact executive snapshot — visible to anyone
            with reports.view (admin / CXO). Numbers come from the same
            metrics endpoint as the Executive Dashboard so they cannot drift. */}
        {showTeam && overviewMetrics && (
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 10,
            marginBottom: 16,
            padding: "10px 12px",
            background: "#F8FAFC",
            border: "1px solid var(--t2t-border)",
            borderRadius: 8,
          }}>
            {snapshotTiles.map((t) => (
              <button
                type="button"
                key={t.label}
                onClick={t.onClick}
                title={t.hint}
                style={{
                  background: "#fff",
                  border: "1px solid var(--t2t-border)",
                  borderRadius: 6,
                  padding: "10px 12px",
                  cursor: "pointer",
                  textAlign: "left",
                  transition: "border-color 0.15s, transform 0.1s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = t.color; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--t2t-border)"; }}
              >
                <div style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  color: "var(--t2t-text-muted)",
                  marginBottom: 4,
                }}>{t.label}</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: t.color, lineHeight: 1 }}>{t.value}</div>
              </button>
            ))}
          </div>
        )}

        {showTeam && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, gap: 12, flexWrap: "wrap" }}>
            <div className="t2t-tab-group" style={{ margin: 0 }}>
              <button className={`t2t-tab${activeTab === "queue" ? " active" : ""}`} onClick={() => setActiveTab("queue")}>My Pending Approvals</button>
              <button className={`t2t-tab${activeTab === "team" ? " active" : ""}`} onClick={() => setActiveTab("team")}>Team Overview</button>
            </div>
            {/* BUG-35: discoverability link to the CXO-style dashboard
                already living in Reports → Executive Dashboard. */}
            <button
              type="button"
              onClick={() => navigate("/t2t-reports?tab=overview")}
              style={{
                background: "none", border: "1px solid var(--t2t-border)",
                borderRadius: 6, padding: "6px 12px",
                fontSize: 12, fontWeight: 600,
                color: "var(--t2t-primary)", cursor: "pointer",
                whiteSpace: "nowrap",
              }}
              title="Open the executive dashboard — KPIs, trends, bottlenecks, all workflows in one view"
            >
              📊 Executive Dashboard →
            </button>
          </div>
        )}

        {activeTab === "queue" && <MyQueueTab navigate={navigate} />}
        {activeTab === "team" && showTeam && <TeamOverviewTab toast={toast} />}
      </div>
    </>
  );
};

// ═══════════════════════════════════════════════════════════════════
// TAB 1 — My Pending Approvals (existing queue)
// ═══════════════════════════════════════════════════════════════════
const MyQueueTab = ({ navigate }) => {
  const [queue, setQueue]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  useEffect(() => { fetchQueue(); }, []);

  const fetchQueue = async () => {
    setLoading(true);
    setError("");
    const resp = await getData("t2t/queue");
    if (resp.error) {
      setError(resp.error.message || "Failed to load queue.");
    } else {
      const raw = resp.data?.data;
      setQueue(Array.isArray(raw) ? raw : []);
    }
    setLoading(false);
  };

  return (
    <>
      <div className="t2t-page-title">My Queue</div>
      <p className="t2t-page-sub">
        {loading ? "Loading..." : `${queue.length} item${queue.length !== 1 ? "s" : ""} waiting for your action.`}
      </p>

      {error && (
        <div style={{ padding: "10px 14px", background: "#FEF2F2", color: "var(--t2t-danger)", borderRadius: 6, fontSize: 12, marginBottom: 12 }}>{error}</div>
      )}

      {!loading && !error && queue.length === 0 && (
        <div className="t2t-empty-state">
          <div className="t2t-empty-icon">📭</div>
          <div className="t2t-empty-title">All caught up!</div>
          <div className="t2t-empty-desc">You have no pending actions at this time.</div>
        </div>
      )}

      {queue.map((item) => {
        let slaColor = "#36B82A";
        let slaLabel = "";
        if (item.sla_status === "breached" || item.sla_status === "overdue") {
          slaColor = "#DC2626";
          slaLabel = item.overdue_by ? `OVERDUE by ${item.overdue_by}` : "OVERDUE";
        } else if (item.sla_status === "approaching" || item.sla_status === "at_risk") {
          slaColor = "#D97706";
          slaLabel = item.time_remaining ? `Due in ${item.time_remaining}` : "SLA approaching";
        } else if (item.due_at) {
          const due = new Date(item.due_at);
          const now = new Date();
          const diff = due - now;
          if (diff < 0) { slaColor = "#DC2626"; slaLabel = "OVERDUE"; }
          else if (item.sla_minutes && diff < item.sla_minutes * 60000 * 0.2) { slaColor = "#D97706"; slaLabel = `Due in ${Math.round(diff / 60000)} min`; }
          else { slaLabel = `Due ${due.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}`; }
        }
        if (item.status === "on_hold") { slaColor = "#D97706"; slaLabel = "ON HOLD"; }

        return (
          <div className="t2t-inbox-card" key={item.request_id} onClick={() => navigate(`/t2t-request/${item.request_id}`)}>
            <div className="t2t-inbox-bar" style={{ background: slaColor }}></div>
            <div style={{ flex: 1 }}>
              <div className="t2t-inbox-title">
                {item.title}
                {item.is_delegated && (
                  <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, color: "#D97706", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 4, padding: "1px 6px" }}>
                    Delegated by {item.original_user || "—"}
                  </span>
                )}
              </div>
              <div className="t2t-inbox-meta">
                <span>📋 {item.workflow_name}</span>
                <span>📍 {item.stage_name}</span>
                <span>👤 {item.submitted_by}</span>
                <span>🕐 {item.submitted_on}</span>
                {slaLabel && (
                  <span style={{ fontWeight: 700, color: slaColor, fontSize: 11 }}>
                    {slaColor === "#DC2626" ? "🔴" : slaColor === "#D97706" ? "🟡" : "🟢"} {slaLabel}
                  </span>
                )}
              </div>
              {item.description && (
                <div style={{ fontSize: 12, color: "var(--t2t-text-secondary)", marginTop: 4 }}>
                  {item.description}
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
              <button className="t2t-btn t2t-btn-success t2t-btn-sm"
                onClick={(e) => { e.stopPropagation(); navigate(`/t2t-request/${item.request_id}`); }}>
                ✓ Approve
              </button>
              <button className="t2t-btn t2t-btn-ghost t2t-btn-sm"
                onClick={(e) => { e.stopPropagation(); navigate(`/t2t-request/${item.request_id}`); }}>
                View
              </button>
            </div>
          </div>
        );
      })}
    </>
  );
};

// ═══════════════════════════════════════════════════════════════════
// TAB 2 — Team Overview (Phase 15-17)
// ═══════════════════════════════════════════════════════════════════
const TeamOverviewTab = ({ toast }) => {
  const navigate = useNavigate();
  const [kpis, setKpis]           = useState({ total_pending: 0, sla_approaching: 0, sla_breached: 0 });
  const [users, setUsers]         = useState([]);
  const [loading, setLoading]     = useState(false);
  const [filters, setFilters]     = useState({ workflow: "", sla_status: "", department: "", date_from: "", date_to: "", stage: "" });
  const [reminders, setReminders] = useState({}); // { userId: sentAt timestamp }
  const [popover, setPopover]     = useState(null); // { userId, requests: [] } — pending count popover

  useEffect(() => { fetchTeamData(); }, [filters]);

  const fetchTeamData = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.workflow)   params.set("workflow", filters.workflow);
    if (filters.sla_status) params.set("sla_status", filters.sla_status);
    if (filters.department) params.set("department", filters.department);
    if (filters.date_from)  params.set("date_from", filters.date_from);
    if (filters.date_to)    params.set("date_to", filters.date_to);
    if (filters.stage)      params.set("stage", filters.stage);

    const [kResp, uResp, rResp] = await Promise.all([
      getData(`t2t/team-overview/kpis?${params.toString()}`),
      getData(`t2t/team-overview/users?${params.toString()}`),
      getData("t2t/team-overview/reminders"),
    ]);
    if (!kResp.error) setKpis(kResp.data?.data || { total_pending: 0, sla_approaching: 0, sla_breached: 0 });
    if (!uResp.error) setUsers(Array.isArray(uResp.data?.data) ? uResp.data.data : []);
    if (!rResp.error) {
      const rList = Array.isArray(rResp.data?.data) ? rResp.data.data : [];
      const map = {};
      rList.forEach((r) => { map[r.sent_to] = r.sent_at; });
      setReminders(map);
    }
    setLoading(false);
  };

  // Fetch pending requests for a user (popover)
  const fetchUserQueue = async (userId) => {
    if (popover?.userId === userId) { setPopover(null); return; }
    const resp = await getData(`t2t/team-overview/users/${userId}/queue`);
    const list = !resp.error ? (Array.isArray(resp.data?.data) ? resp.data.data : []) : [];
    setPopover({ userId, requests: list });
  };

  // Phase 16 — Reminder
  const sendReminder = async (userId, userName) => {
    const resp = await postData("t2t/team-overview/remind", { user_id: userId });
    if (resp.error) { toast(resp.error.message || "Failed to send reminder.", "error"); return; }
    toast(`Reminder sent to ${userName}.`, "success");
    setReminders((prev) => ({ ...prev, [userId]: new Date().toISOString() }));
  };

  // Phase 17 — Escalation
  const [escModal, setEscModal]   = useState(null); // userId
  const [escNote, setEscNote]     = useState("");
  const [escSending, setEscSending] = useState(false);

  const sendEscalation = async () => {
    if (escNote.trim().length < 5) return;
    setEscSending(true);
    const resp = await postData("t2t/team-overview/escalate", { user_id: escModal, note: escNote.trim() });
    if (resp.error) { toast(resp.error.message || "Escalation failed.", "error"); }
    else { toast("Escalated to manager.", "success"); }
    setEscSending(false);
    setEscModal(null);
    setEscNote("");
    fetchTeamData();
  };

  const isReminderCooling = (userId) => {
    const sentAt = reminders[userId];
    if (!sentAt) return false;
    return (Date.now() - new Date(sentAt).getTime()) < 24 * 60 * 60 * 1000; // 24hr
  };

  return (
    <>
      <div className="t2t-page-title">Team Overview</div>
      <p className="t2t-page-sub">Monitor your team&apos;s pending approvals, SLA status, and take action.</p>

      {/* KPI Cards — BUG-34 follow-up: tooltips clarify each tile counts unique requests */}
      <div className="t2t-stats-grid" style={{ marginBottom: 20 }}>
        <div
          className="t2t-stat-card"
          style={{ cursor: "pointer" }}
          onClick={() => setFilters({ workflow: "", sla_status: "", department: "" })}
          title="Unique pending requests across the system. Rows in the table below may sum higher when a request is shared by multiple approvers."
        >
          <div className="t2t-stat-label">Total Pending</div>
          <div className="t2t-stat-value" style={{ color: "#2563EB" }}>{kpis.total_pending}</div>
        </div>
        <div
          className="t2t-stat-card"
          style={{ cursor: "pointer" }}
          onClick={() => setFilters({ ...filters, sla_status: "approaching" })}
          title="Unique requests within 20% of their stage SLA."
        >
          <div className="t2t-stat-label">SLA Approaching</div>
          <div className="t2t-stat-value" style={{ color: "#D97706" }}>{kpis.sla_approaching}</div>
        </div>
        <div
          className="t2t-stat-card"
          style={{ cursor: "pointer" }}
          onClick={() => setFilters({ ...filters, sla_status: "breached" })}
          title="Unique requests past their stage SLA."
        >
          <div className="t2t-stat-label">SLA Breached</div>
          <div className="t2t-stat-value" style={{ color: "#DC2626" }}>{kpis.sla_breached}</div>
        </div>
      </div>

      {/* Filter Bar */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <select className="t2t-fc" style={{ flex: 1, minWidth: 130 }} value={filters.sla_status}
          onChange={(e) => setFilters({ ...filters, sla_status: e.target.value })}>
          <option value="">All SLA Status</option>
          <option value="on_track">On Track</option>
          <option value="approaching">Approaching</option>
          <option value="breached">Breached</option>
          <option value="on_hold">On Hold</option>
        </select>
        <select className="t2t-fc" style={{ flex: 1, minWidth: 130 }} value={filters.workflow}
          onChange={(e) => setFilters({ ...filters, workflow: e.target.value })}>
          <option value="">All Workflows</option>
        </select>
        <select className="t2t-fc" style={{ flex: 1, minWidth: 130 }} value={filters.stage}
          onChange={(e) => setFilters({ ...filters, stage: e.target.value })}>
          <option value="">All Stages</option>
        </select>
        <input className="t2t-fc" type="date" style={{ flex: 1, minWidth: 130 }} value={filters.date_from}
          onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} title="From date" placeholder="From" />
        <input className="t2t-fc" type="date" style={{ flex: 1, minWidth: 130 }} value={filters.date_to}
          onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} title="To date" placeholder="To" />
      </div>

      {/* User-Wise Table */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 32, color: "var(--t2t-text-muted)", fontSize: 12 }}>Loading...</div>
      ) : (
        <div className="t2t-table">
          <div className="t2t-table-head" style={{ gridTemplateColumns: "1.5fr 1fr 1fr .8fr .6fr .8fr .7fr" }}>
            <div>User</div>
            <div>Role</div>
            <div>Dept / Org</div>
            <div>Pending</div>
            <div>Oldest</div>
            <div>SLA Status</div>
            <div>Actions</div>
          </div>
          {users.length === 0 ? (
            <div style={{ padding: 32, textAlign: "center", color: "var(--t2t-text-muted)", fontSize: 13 }}>
              No pending approvals in the system.
            </div>
          ) : users.map((u) => {
            // BUG-34: synthetic "Unassigned" bucket gets its own amber row treatment.
            if (u.is_unassigned) {
              return (
                <div
                  className="t2t-table-row"
                  key="__unassigned__"
                  style={{
                    gridTemplateColumns: "1.5fr 1fr 1fr .8fr .6fr .8fr .7fr",
                    background: "#FFFBEB",
                    borderLeft: "3px solid #D97706",
                  }}
                  title="These requests are stuck because their current stage has no approver role configured. Fix in Workflow Builder → edit the stage → set role_ids."
                >
                  <div style={{ fontWeight: 700, color: "#92400E" }}>
                    ⚠ {u.user_name}
                  </div>
                  <div style={{ color: "#92400E", fontSize: 11, fontStyle: "italic" }}>—</div>
                  <div style={{ color: "#92400E", fontSize: 11, fontStyle: "italic" }}>—</div>
                  <div style={{ fontWeight: 700, color: "#92400E" }}>{u.pending_count}</div>
                  <div style={{ fontSize: 11, color: "#92400E" }}>{u.oldest_pending || "—"}</div>
                  <div>
                    <span style={{ fontSize: 12 }}>{u.sla_worst === "breached" ? "🔴" : u.sla_worst === "approaching" ? "🟡" : "🟢"}</span>
                  </div>
                  <div>
                    <button
                      title="Open Workflow Builder to set approver roles"
                      onClick={() => navigate("/t2t-home")}
                      style={{ background: "none", border: "1px solid #D97706", color: "#92400E", borderRadius: 4, fontSize: 10, padding: "3px 8px", cursor: "pointer", fontWeight: 700 }}
                    >
                      Fix in Builder
                    </button>
                  </div>
                </div>
              );
            }

            const slaIcon = u.sla_worst === "breached" ? "🔴" : u.sla_worst === "approaching" ? "🟡" : "🟢";
            const cooling = isReminderCooling(u.user_id);
            const isPopoverOpen = popover?.userId === u.user_id;
            return (
              <div className="t2t-table-row" key={u.user_id} style={{ gridTemplateColumns: "1.5fr 1fr 1fr .8fr .6fr .8fr .7fr" }}>
                <div>
                  <span
                    style={{ fontWeight: 600, color: "var(--t2t-primary)", cursor: "pointer", textDecoration: "underline dotted" }}
                    onClick={() => navigate(`/t2t-admin?user=${u.user_id}`)}
                    title="View user details"
                  >{u.user_name}</span>
                </div>
                <div style={{ color: "var(--t2t-text-secondary)" }}>{u.role_name || "—"}</div>
                <div style={{ fontSize: 11, color: "#64748B" }}>{u.org_unit_name || u.department || "—"}</div>
                <div style={{ position: "relative" }}>
                  <span
                    style={{ fontWeight: 700, color: "#2563EB", cursor: "pointer", textDecoration: "underline dotted" }}
                    onClick={() => fetchUserQueue(u.user_id)}
                    title="Click to view pending requests"
                  >{u.pending_count}</span>
                  {/* Pending Requests Popover */}
                  {isPopoverOpen && (
                    <div style={{ position: "absolute", top: "100%", left: 0, zIndex: 100, background: "#fff", border: "1px solid var(--t2t-border)", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,.12)", width: 320, maxHeight: 240, overflow: "auto", padding: 8, marginTop: 4 }}>
                      <div style={{ fontWeight: 700, fontSize: 11, color: "var(--t2t-text-muted)", marginBottom: 6 }}>
                        Pending items for {u.user_name}
                        <button onClick={() => setPopover(null)} style={{ float: "right", background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#94A3B8" }}>✕</button>
                      </div>
                      {popover.requests.length === 0 ? (
                        <div style={{ fontSize: 11, color: "#94A3B8", padding: 8 }}>No pending requests found.</div>
                      ) : popover.requests.map((r) => (
                        <div key={r.request_id} style={{ padding: "6px 8px", fontSize: 11, borderBottom: "1px solid #F1F5F9", cursor: "pointer" }}
                          onClick={() => navigate(`/t2t-request/${r.request_id}`)}>
                          <div style={{ fontWeight: 600 }}>{r.title || r.reference_number || `#${r.request_id}`}</div>
                          <div style={{ color: "#64748B" }}>{r.stage_name || ""} {r.sla_status === "breached" ? "🔴" : r.sla_status === "approaching" ? "🟡" : ""}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 11, color: "#64748B" }}>{u.oldest_pending || "—"}</div>
                <div>
                  <span style={{ fontSize: 12 }}>{slaIcon}</span>
                  {u.escalated && <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, color: "#DC2626", background: "#FEF2F2", padding: "1px 6px", borderRadius: 4 }}>ESCALATED</span>}
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    title={cooling ? "Reminder sent — wait 24hrs" : "Send reminder"}
                    disabled={cooling}
                    onClick={() => sendReminder(u.user_id, u.user_name)}
                    style={{ background: "none", border: "none", cursor: cooling ? "not-allowed" : "pointer", fontSize: 16, opacity: cooling ? 0.4 : 1 }}
                  >🔔</button>
                  <button
                    title="Escalate to manager"
                    onClick={() => { setEscModal(u.user_id); setEscNote(""); }}
                    style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16 }}
                  >⬆</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* BUG-34 follow-up: explain why row counts can sum higher than the KPI */}
      {kpis.total_pending > 0 && users.length > 0 && (
        <div style={{
          marginTop: 12,
          padding: "10px 12px",
          background: "#F1F5F9",
          border: "1px solid #E2E8F0",
          borderRadius: 6,
          fontSize: 11,
          color: "var(--t2t-text-secondary)",
          lineHeight: 1.5,
        }}>
          💡 <strong>Why row counts may add up to more than {kpis.total_pending}:</strong> some requests are visible to multiple approvers (e.g. when a stage allows any user with the same role to act). Each approver sees it in their row, but the KPI above counts each request only once. Click any pending count to see the actual requests.
        </div>
      )}

      {/* BUG-34: link to the full Request Tracker for raw per-request view */}
      {kpis.total_pending > 0 && (
        <div style={{ marginTop: 12, textAlign: "right" }}>
          <button
            type="button"
            onClick={() => navigate("/t2t-reports?tab=tracker&status=in_progress")}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: "var(--t2t-primary)", fontSize: 12, fontWeight: 600,
              padding: "4px 0",
            }}
          >
            View all {kpis.total_pending} pending requests in Reports →
          </button>
        </div>
      )}

      {/* Escalation Modal */}
      {escModal && (
        <div className="t2t-overlay" onClick={() => setEscModal(null)}>
          <div className="t2t-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <div className="t2t-modal-head">
              <div className="t2t-modal-title">Escalate to Manager</div>
              <button className="t2t-close-btn" onClick={() => setEscModal(null)}>×</button>
            </div>
            <div className="t2t-modal-body">
              <p style={{ fontSize: 13, color: "var(--t2t-text-secondary)", marginBottom: 12 }}>
                This will notify both the user and their manager about overdue items.
              </p>
              <div className="t2t-fg">
                <label>Escalation Note * (min 5 characters)</label>
                <textarea
                  className="t2t-fc" rows="3"
                  placeholder="Explain why you are escalating..."
                  value={escNote}
                  onChange={(e) => setEscNote(e.target.value)}
                />
              </div>
            </div>
            <div className="t2t-modal-foot">
              <button className="t2t-btn t2t-btn-ghost" onClick={() => setEscModal(null)}>Cancel</button>
              <button className="t2t-btn t2t-btn-danger" onClick={sendEscalation} disabled={escSending || escNote.trim().length < 5}>
                {escSending ? "Escalating..." : "Escalate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

MyQueueTab.propTypes      = { navigate: PropTypes.func.isRequired };
TeamOverviewTab.propTypes = { toast: PropTypes.func.isRequired };

export default T2TQueue;
